<script>
import { mapGetters } from 'vuex';
import { useBranding } from 'shared/composables/useBranding';
import SignupForm from './components/Signup/Form.vue';
import Spinner from 'shared/components/Spinner.vue';

export default {
  components: {
    SignupForm,
    Spinner,
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return { replaceInstallationName };
  },
  data() {
    return { isLoading: false };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    isAChatwootInstance() {
      return false;
    },
  },
  beforeMount() {
    this.isLoading = this.isAChatwootInstance;
  },
  methods: {
    resizeContainers() {
      this.isLoading = false;
    },
  },
};
</script>

<template>
  <div class="sp-auth-page">
    <!-- Left visual panel -->
    <div
      class="sp-auth-visual"
      style="
        background: linear-gradient(
          145deg,
          #eeebf9 0%,
          #e5dffd 25%,
          #ddd8f4 50%,
          #d6d0f4 75%,
          #ded9f5 100%
        );
      "
    >
      <div class="sp-grid-bg" />
      <div class="sp-glow-orb sp-glow-1" />
      <div class="sp-glow-orb sp-glow-2" />
      <div class="sp-glow-orb sp-glow-3" />
      <div class="sp-particle sp-particle-1" />
      <div class="sp-particle sp-particle-2" />
      <div class="sp-particle sp-particle-3" />
      <div class="sp-particle sp-particle-4" />
      <div class="sp-particle sp-particle-5" />

      <div class="sp-visual-inner">
        <!-- Badge -->
        <div class="sp-animate-in" style="margin-bottom: 14px">
          <div class="sp-badge">
            <span class="sp-badge-dot sp-badge-dot-green" />
            Free forever plan available
          </div>
        </div>

        <!-- Hero text -->
        <div
          class="sp-animate-in sp-delay-1"
          style="text-align: center; margin-bottom: 20px"
        >
          <h1 class="sp-hero-text" style="font-size: 52px">
            Start Helping<br />
            <span class="sp-gradient-text">Customers.</span>
          </h1>
          <p class="sp-hero-sub">
            No credit card needed. Deliver exceptional support with
            enterprise-grade security.
          </p>
        </div>

        <!-- Testimonial -->
        <div
          class="sp-animate-in sp-delay-2"
          style="width: 100%; max-width: 380px; margin-bottom: 20px"
        >
          <div class="sp-glass-card" style="padding: 20px 22px">
            <!-- Stars -->
            <div style="display: flex; gap: 2px; margin-bottom: 10px">
              <svg
                v-for="i in 5"
                :key="i"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="#f59e0b"
              >
                <path
                  d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                />
              </svg>
            </div>
            <div
              style="
                font-size: 13px;
                color: #334155;
                line-height: 1.6;
                margin-bottom: 14px;
                font-style: italic;
              "
            >
              "We cut first-response time by 68% in the first month. Our support
              team finally has a tool that actually works at our scale."
            </div>
            <div style="display: flex; align-items: center; gap: 12px">
              <!-- eslint-disable vue/no-useless-v-bind -->
              <img
                :src="'/testimonial-avatar.png'"
                alt="Priya Patel"
                style="
                  width: 42px;
                  height: 42px;
                  border-radius: 50%;
                  object-fit: cover;
                  border: 2px solid rgba(99, 102, 241, 0.2);
                "
              />
              <!-- eslint-enable vue/no-useless-v-bind -->
              <div>
                <div style="font-size: 12px; font-weight: 700; color: #0f172a">
                  Priya Patel
                </div>
                <div style="font-size: 11px; color: #64748b">
                  VP of Customer Experience · Canva
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Trusted by logos -->
        <div
          class="sp-animate-in sp-delay-3"
          style="width: 100%; max-width: 380px"
        >
          <div
            style="
              font-size: 10px;
              font-weight: 600;
              color: #94a3b8;
              letter-spacing: 0.08em;
              text-transform: uppercase;
              text-align: center;
              margin-bottom: 10px;
            "
          >
            Trusted by teams at
          </div>
          <!-- eslint-disable vue/no-useless-v-bind -->
          <img
            :src="'/trusted-logos.png'"
            alt="Trusted by EA, SMART, SEGA, GoDaddy, Disney and more"
            style="width: 100%; opacity: 0.55; filter: grayscale(100%)"
          />
          <!-- eslint-enable vue/no-useless-v-bind -->
        </div>
      </div>
    </div>

    <!-- Right form panel -->
    <div class="sp-auth-form-side">
      <div class="sp-auth-form-inner-wrap">
        <div v-show="!isLoading" class="sp-auth-form-container">
          <!-- Logo -->
          <div
            class="sp-animate-in"
            style="text-align: center; margin-bottom: 16px"
          >
            <img
              :src="globalConfig.logo"
              :alt="globalConfig.installationName"
              style="height: 56px; display: inline-block"
              class="dark:hidden"
            />
            <img
              v-if="globalConfig.logoDark"
              :src="globalConfig.logoDark"
              :alt="globalConfig.installationName"
              style="height: 56px; display: none"
              class="dark:inline-block"
            />
          </div>

          <!-- Heading -->
          <div
            class="sp-animate-in sp-delay-1"
            style="text-align: center; margin-bottom: 18px"
          >
            <h2
              style="
                font-size: 22px;
                font-weight: 800;
                color: #0f172a;
                letter-spacing: -0.03em;
                margin-bottom: 3px;
              "
            >
              Create Your Account
            </h2>
            <p style="font-size: 13px; color: #64748b">
              Set Up Your Supportly Workspace in Minutes.
            </p>
          </div>

          <SignupForm />

          <div
            class="sp-animate-in sp-delay-5"
            style="
              text-align: center;
              margin-top: 14px;
              padding-top: 12px;
              border-top: 1px solid #f1f5f9;
              font-size: 12px;
              color: #64748b;
            "
          >
            {{ $t('REGISTER.HAVE_AN_ACCOUNT') }}
            <router-link
              class="sp-link"
              style="margin-left: 4px; font-weight: 700"
              to="/app/login"
            >
              {{ replaceInstallationName($t('LOGIN.TITLE')) }} →
            </router-link>
          </div>
        </div>

        <div
          v-show="isLoading"
          style="
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 64px 0;
          "
        >
          <Spinner color-scheme="primary" size="" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes sp-particle-float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.4;
  }
  25% {
    transform: translate(20px, -30px) scale(1.5);
    opacity: 0.8;
  }
  50% {
    transform: translate(-10px, -60px) scale(1);
    opacity: 0.6;
  }
  75% {
    transform: translate(15px, -20px) scale(1.3);
    opacity: 0.9;
  }
}
@keyframes sp-float-drift {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -20px) scale(1.05);
  }
  66% {
    transform: translate(-20px, 15px) scale(0.95);
  }
}
@keyframes sp-slide-in {
  from {
    opacity: 0;
    transform: translateY(32px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes sp-pulse-soft {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}
@keyframes sp-float-card-anim {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}
@keyframes sp-float-card-alt-anim {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
}

.sp-auth-page {
  display: flex;
  height: 100vh;
  width: 100%;
  background: #ffffff;
  overflow: hidden;
}
.sp-auth-visual {
  display: none;
  width: 50%;
  position: relative;
  overflow: hidden;
}
@media (min-width: 1024px) {
  .sp-auth-visual {
    display: flex;
  }
}
.sp-auth-form-side {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100vh;
  background: #ffffff;
}
.sp-auth-form-inner-wrap {
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 24px;
  box-sizing: border-box;
}
@media (min-width: 1024px) {
  .sp-auth-form-inner-wrap {
    padding: 28px 48px;
  }
}
@media (min-width: 1280px) {
  .sp-auth-form-inner-wrap {
    padding: 28px 64px;
  }
}
.sp-auth-form-container {
  width: 100%;
  max-width: 420px;
}
.sp-grid-bg {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(99, 102, 241, 0.05) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(99, 102, 241, 0.05) 1px, transparent 1px);
  background-size: 40px 40px;
}
.sp-glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
}
.sp-glow-1 {
  width: 500px;
  height: 500px;
  background: rgba(139, 92, 246, 0.3);
  top: -120px;
  right: -100px;
  animation: sp-float-drift 8s ease-in-out infinite;
}
.sp-glow-2 {
  width: 450px;
  height: 450px;
  background: rgba(99, 102, 241, 0.25);
  bottom: -60px;
  left: -80px;
  animation: sp-float-drift 10s ease-in-out infinite reverse;
}
.sp-glow-3 {
  width: 300px;
  height: 300px;
  background: rgba(59, 130, 246, 0.2);
  top: 35%;
  left: 25%;
  animation: sp-float-drift 12s ease-in-out infinite 2s;
}
.sp-particle {
  position: absolute;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.4);
  pointer-events: none;
}
.sp-particle-1 {
  width: 6px;
  height: 6px;
  top: 15%;
  left: 20%;
  animation: sp-particle-float 6s ease-in-out infinite;
}
.sp-particle-2 {
  width: 4px;
  height: 4px;
  top: 35%;
  right: 15%;
  animation: sp-particle-float 8s ease-in-out infinite 1s;
}
.sp-particle-3 {
  width: 8px;
  height: 8px;
  bottom: 25%;
  left: 30%;
  animation: sp-particle-float 7s ease-in-out infinite 2s;
}
.sp-particle-4 {
  width: 5px;
  height: 5px;
  top: 60%;
  right: 25%;
  animation: sp-particle-float 9s ease-in-out infinite 0.5s;
}
.sp-particle-5 {
  width: 3px;
  height: 3px;
  top: 80%;
  left: 15%;
  animation: sp-particle-float 5s ease-in-out infinite 3s;
}
.sp-visual-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 28px 36px;
}
.sp-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  color: #6366f1;
  border: 1px solid rgba(99, 102, 241, 0.15);
  backdrop-filter: blur(12px);
}
.sp-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}
.sp-badge-dot-green {
  background: #22c55e;
  animation: sp-pulse-soft 2s infinite;
}
.sp-hero-text {
  font-size: 60px;
  font-weight: 900;
  line-height: 1.05;
  color: #0f172a;
  letter-spacing: -0.04em;
  margin-bottom: 10px;
}
.sp-hero-sub {
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
  max-width: 340px;
  margin: 0 auto;
}
.sp-gradient-text {
  background: linear-gradient(135deg, #4f46e5, #7c3aed, #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.sp-glass-card {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}
.sp-float-card {
  animation: sp-float-card-anim 7s ease-in-out infinite;
}
.sp-float-card-alt {
  animation: sp-float-card-alt-anim 6s ease-in-out infinite;
}
.sp-feature-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
}
.sp-trust-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  padding: 5px 12px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 100px;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.8);
}
.sp-animate-in {
  animation: sp-slide-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  opacity: 0;
}
.sp-delay-1 {
  animation-delay: 0.1s;
}
.sp-delay-2 {
  animation-delay: 0.2s;
}
.sp-delay-3 {
  animation-delay: 0.3s;
}
.sp-delay-4 {
  animation-delay: 0.4s;
}
.sp-delay-5 {
  animation-delay: 0.5s;
}

.sp-link {
  color: #6366f1;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s;
}
.sp-link:hover {
  color: #4f46e5;
}

/* Style the SignupForm's inputs and button to match Zignature */
:deep(input[type='text']),
:deep(input[type='email']),
:deep(input[type='password']) {
  width: 100% !important;
  padding: 12px 16px !important;
  border: 2px solid #e2e8f0 !important;
  border-radius: 12px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #0f172a !important;
  background: #ffffff !important;
  transition: all 0.25s !important;
  outline: none !important;
  box-shadow: none !important;
}
:deep(input[type='text']:focus),
:deep(input[type='email']:focus),
:deep(input[type='password']:focus) {
  border-color: #6366f1 !important;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12) !important;
}
:deep(button[type='submit']) {
  width: 100% !important;
  padding: 12px 24px !important;
  background: linear-gradient(
    135deg,
    #4f46e5 0%,
    #6366f1 50%,
    #7c3aed 100%
  ) !important;
  color: #ffffff !important;
  border: none !important;
  border-radius: 12px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  cursor: pointer !important;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3) !important;
  transition: all 0.3s !important;
}
:deep(button[type='submit']:hover) {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.45) !important;
}
</style>
